import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  Column,
  ErrorTag,
  ErrorText,
  Fields,
  FormBody,
  OutlineButton,
  PrimaryButton,
  TextInput,
} from 'components/layout/Auth/AuthForm/styles';

import {
  formatDocumentForDisplay,
  sanitizeDocument,
  validateDocument,
} from 'utils/document';

import {
  CONTENT_WIDTH,
  FieldWithHelp,
  HelpLink,
  NarrowActions,
  NarrowField,
  OnboardingIntro,
  OnboardingSubtitle,
  OnboardingTitle,
} from '../styles';

type IdentificacaoProps = {
  customerCode: string;
  document: string;
  loading: boolean;
  erro: string | null;
  onChangeCustomerCode: (valor: string) => void;
  onChangeDocument: (valor: string) => void;
  onSubmit: () => void;
  onHelp: () => void;
};

const Identificacao = ({
  customerCode,
  document,
  loading,
  erro,
  onChangeCustomerCode,
  onChangeDocument,
  onSubmit,
  onHelp,
}: IdentificacaoProps): JSX.Element => {
  const history = useHistory();
  const [tocouDocumento, setTocouDocumento] = useState(false);

  const erroDocumento = tocouDocumento ? validateDocument(document) : '';
  const podeEnviar = !loading && !!customerCode && !!document && !erroDocumento;

  const handleCustomerCode = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void =>
    onChangeCustomerCode(event.target.value.replace(/[^\d]/g, '').slice(0, 30));

  const handleDocument = (event: React.ChangeEvent<HTMLInputElement>): void =>
    onChangeDocument(sanitizeDocument(event.target.value));

  const handleDocumentBlur = (): void => {
    setTocouDocumento(true);
    onChangeDocument(formatDocumentForDisplay(document));
  };

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setTocouDocumento(true);
    if (!validateDocument(document) && customerCode) onSubmit();
  };

  return (
    <Column gap={24} width={CONTENT_WIDTH}>
      <OnboardingIntro>
        <OnboardingTitle>
          Queremos agilizar seu processo! É simples e rápido.
        </OnboardingTitle>
        <OnboardingSubtitle>
          Para começar, digite o código do cliente e seu CPF ou CNPJ.
        </OnboardingSubtitle>
      </OnboardingIntro>

      <form onSubmit={handleSubmit} noValidate>
        <FormBody>
          <Fields>
            <FieldWithHelp>
              <HelpLink type="button" onClick={onHelp}>
                Onde encontrar?
              </HelpLink>
              <NarrowField>
                <TextInput
                  id="codigo-cliente"
                  name="codigo-cliente"
                  type="text"
                  inputMode="numeric"
                  label="Código do cliente"
                  placeholder="Digite"
                  required
                  fullWidth
                  autoFocus
                  value={customerCode}
                  onChange={handleCustomerCode}
                />
              </NarrowField>
            </FieldWithHelp>

            <NarrowField>
              <TextInput
                id="documento"
                name="documento"
                type="text"
                label="CPF/ CNPJ"
                placeholder="Digite"
                required
                fullWidth
                error={!!erroDocumento}
                value={document}
                onChange={handleDocument}
                onFocus={() => onChangeDocument(sanitizeDocument(document))}
                onBlur={handleDocumentBlur}
              />
              {!!erroDocumento && <ErrorText>{erroDocumento}</ErrorText>}
            </NarrowField>
          </Fields>

          {!!erro && <ErrorTag role="alert">{erro}</ErrorTag>}

          <NarrowActions>
            <OutlineButton
              type="button"
              variant="secondary"
              shape="pill"
              fullWidth
              onClick={() => history.push('/')}
            >
              Voltar
            </OutlineButton>
            <PrimaryButton
              type="submit"
              shape="pill"
              fullWidth
              disabled={!podeEnviar}
            >
              {loading ? 'Aguarde...' : 'Continuar'}
            </PrimaryButton>
          </NarrowActions>
        </FormBody>
      </form>
    </Column>
  );
};

export default Identificacao;
